import './style.css';
import { IDPhotoCanvas } from './canvas';
import { renderPrintSheet, PAPERS, type LayoutOptions } from './typesetter';

document.addEventListener('DOMContentLoaded', () => {
  // --- UI Elements ---
  // Navigation & Theme
  const themeToggle = document.getElementById('theme-toggle') as HTMLButtonElement;
  const stepIndicators = document.querySelectorAll('.step-indicator');
  const stepPanels = document.querySelectorAll('.step-panel');
  const btnPrev = document.getElementById('btn-prev') as HTMLButtonElement;
  const btnNext = document.getElementById('btn-next') as HTMLButtonElement;
  const exportActions = document.getElementById('export-actions') as HTMLElement;
  const toast = document.getElementById('toast') as HTMLElement;
  const toastMessage = document.getElementById('toast-message') as HTMLElement;

  // Viewport Toolbar
  const btnModeCrop = document.getElementById('btn-mode-crop') as HTMLButtonElement;
  const btnModeSheet = document.getElementById('btn-mode-sheet') as HTMLButtonElement;
  const btnZoomIn = document.getElementById('btn-zoom-in') as HTMLButtonElement;
  const btnZoomOut = document.getElementById('btn-zoom-out') as HTMLButtonElement;
  const btnZoomReset = document.getElementById('btn-zoom-reset') as HTMLButtonElement;
  const zoomPercentage = document.getElementById('zoom-percentage') as HTMLElement;

  // Step 1: Upload & Capture
  const dropzone = document.getElementById('dropzone') as HTMLElement;
  const fileInput = document.getElementById('file-input') as HTMLInputElement;
  const btnBrowse = document.getElementById('btn-browse') as HTMLButtonElement;
  const btnCamera = document.getElementById('btn-camera') as HTMLButtonElement;
  const sampleCards = document.querySelectorAll('.sample-card');
  const sampleImgMan = document.getElementById('sample-img-man') as HTMLImageElement;
  const sampleImgWoman = document.getElementById('sample-img-woman') as HTMLImageElement;

  // Camera Modal
  const cameraModal = document.getElementById('camera-modal') as HTMLElement;
  const btnCloseCamera = document.getElementById('btn-close-camera') as HTMLButtonElement;
  const webcamVideo = document.getElementById('webcam-video') as HTMLVideoElement;
  const cameraSelect = document.getElementById('camera-select') as HTMLSelectElement;
  const btnCaptureSnap = document.getElementById('btn-capture-snap') as HTMLButtonElement;
  const cameraLoading = document.getElementById('camera-loading') as HTMLElement;

  // Step 2: Crop
  const sizePreset = document.getElementById('size-preset') as HTMLSelectElement;
  const customSizeSettings = document.getElementById('custom-size-settings') as HTMLElement;
  const customWidthInput = document.getElementById('custom-width') as HTMLInputElement;
  const customHeightInput = document.getElementById('custom-height') as HTMLInputElement;
  const toggleGuides = document.getElementById('toggle-guides') as HTMLInputElement;
  const btnRotateCCW = document.getElementById('btn-rotate-ccw') as HTMLButtonElement;
  const btnRotateCW = document.getElementById('btn-rotate-cw') as HTMLButtonElement;
  const btnFlipH = document.getElementById('btn-flip-h') as HTMLButtonElement;
  const btnResetCrop = document.getElementById('btn-reset-crop') as HTMLButtonElement;
  const fineRotateSlider = document.getElementById('fine-rotate-slider') as HTMLInputElement;
  const fineRotateVal = document.getElementById('fine-rotate-val') as HTMLElement;

  // Step 3: Background & Filters
  const tabBtns = document.querySelectorAll('.tab-btn');
  const tabContents = document.querySelectorAll('.tab-content');
  const toggleAICutout = document.getElementById('toggle-ai-cutout') as HTMLInputElement;
  const aiLoadingIndicator = document.getElementById('ai-loading-indicator') as HTMLElement;
  const btnChromaPicker = document.getElementById('btn-chroma-picker') as HTMLButtonElement;
  const chromaSettings = document.getElementById('chroma-settings') as HTMLElement;
  const chromaTolerance = document.getElementById('chroma-tolerance') as HTMLInputElement;
  const chromaToleranceVal = document.getElementById('chroma-tolerance-val') as HTMLElement;
  const chromaFeather = document.getElementById('chroma-feather') as HTMLInputElement;
  const chromaFeatherVal = document.getElementById('chroma-feather-val') as HTMLElement;
  const bgPresetBtns = document.querySelectorAll('.bg-preset-btn');
  const bgCustomColor = document.getElementById('bg-custom-color') as HTMLInputElement;
  const toggleVignette = document.getElementById('toggle-vignette') as HTMLInputElement;

  // Filters inputs
  const filterBrightness = document.getElementById('filter-brightness') as HTMLInputElement;
  const filterBrightnessVal = document.getElementById('filter-brightness-val') as HTMLElement;
  const filterContrast = document.getElementById('filter-contrast') as HTMLInputElement;
  const filterContrastVal = document.getElementById('filter-contrast-val') as HTMLElement;
  const filterSaturation = document.getElementById('filter-saturation') as HTMLInputElement;
  const filterSaturationVal = document.getElementById('filter-saturation-val') as HTMLElement;
  const filterSmooth = document.getElementById('filter-smooth') as HTMLInputElement;
  const filterSmoothVal = document.getElementById('filter-smooth-val') as HTMLElement;
  const filterSharpen = document.getElementById('filter-sharpen') as HTMLInputElement;
  const filterSharpenVal = document.getElementById('filter-sharpen-val') as HTMLElement;
  const filterGrayscale = document.getElementById('filter-grayscale') as HTMLInputElement;
  const btnResetFilters = document.getElementById('btn-reset-filters') as HTMLButtonElement;

  // Step 4: Typeset
  const paperSize = document.getElementById('paper-size') as HTMLSelectElement;
  const paperOrientation = document.getElementById('paper-orientation') as HTMLSelectElement;
  const layoutMargin = document.getElementById('layout-margin') as HTMLInputElement;
  const layoutMarginVal = document.getElementById('layout-margin-val') as HTMLElement;
  const layoutGap = document.getElementById('layout-gap') as HTMLInputElement;
  const layoutGapVal = document.getElementById('layout-gap-val') as HTMLElement;
  const toggleCutLines = document.getElementById('toggle-cut-lines') as HTMLInputElement;
  const togglePhotoBorder = document.getElementById('toggle-photo-border') as HTMLInputElement;
  const btnQtyDec = document.getElementById('btn-qty-dec') as HTMLButtonElement;
  const btnQtyInc = document.getElementById('btn-qty-inc') as HTMLButtonElement;
  const photoQuantity = document.getElementById('photo-quantity') as HTMLInputElement;
  const btnAutoFill = document.getElementById('btn-auto-fill') as HTMLButtonElement;

  // Bottom Export Actions
  const btnDownloadSingle = document.getElementById('btn-download-single') as HTMLButtonElement;
  const btnDownloadSheet = document.getElementById('btn-download-sheet') as HTMLButtonElement;
  const btnPrint = document.getElementById('btn-print') as HTMLButtonElement;
  const printImage = document.getElementById('print-image') as HTMLImageElement;

  // --- Initialize App Objects ---
  let canvasController: IDPhotoCanvas;
  try {
    canvasController = new IDPhotoCanvas('main-canvas', 'viewport');
  } catch (err) {
    console.error(err);
    alert('Failed to initialize canvas workspace. Please reload.');
    return;
  }

  let currentStep = 1;
  let webcamStream: MediaStream | null = null;
  let isPickingChromaColor = false;

  // Set sample images (Vite serves public files relative to root base)
  sampleImgMan.src = './sample-man.png';
  sampleImgWoman.src = './sample-woman.png';

  // --- Toast Notifications ---
  function showToast(message: string) {
    toastMessage.textContent = message;
    toast.classList.remove('hidden');
    setTimeout(() => {
      toast.classList.add('hidden');
    }, 4000);
  }

  canvasController.onToast = showToast;

  canvasController.onStateChange = () => {
    // Update zoom indicators in toolbar
    const zoom = Math.round(canvasController.getZoom() * 100);
    zoomPercentage.textContent = `${zoom}%`;
  };

  // --- Theme Selection ---
  const savedTheme = localStorage.getItem('theme') || 'dark';
  if (savedTheme === 'light') {
    document.body.classList.remove('dark-mode');
    document.body.classList.add('light-mode');
  }
  
  themeToggle.addEventListener('click', () => {
    if (document.body.classList.contains('dark-mode')) {
      document.body.classList.remove('dark-mode');
      document.body.classList.add('light-mode');
      localStorage.setItem('theme', 'light');
    } else {
      document.body.classList.remove('light-mode');
      document.body.classList.add('dark-mode');
      localStorage.setItem('theme', 'dark');
    }
    // Update canvas background drawing color
    canvasController.update();
  });

  // --- Tab Selection (Step 3: Background & Filters) ---
  tabBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      tabBtns.forEach(b => b.classList.remove('active'));
      tabContents.forEach(c => c.classList.remove('active'));
      
      btn.classList.add('active');
      const tabId = btn.getAttribute('data-tab');
      if (tabId) {
        document.getElementById(tabId)?.classList.add('active');
      }
    });
  });

  // --- Step Navigation State Management ---
  function updateStepUI() {
    stepIndicators.forEach((ind, index) => {
      const idx = index + 1;
      ind.classList.remove('active', 'completed');
      if (idx === currentStep) {
        ind.classList.add('active');
      } else if (idx < currentStep) {
        ind.classList.add('completed');
      }
    });

    stepPanels.forEach((panel, index) => {
      panel.classList.toggle('active', index + 1 === currentStep);
    });

    // Button states
    btnPrev.classList.toggle('hidden', currentStep === 1);
    if (currentStep === 4) {
      btnNext.innerHTML = `Finish Sheet <svg viewBox="0 0 24 24" class="btn-icon-svg" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>`;
    } else {
      btnNext.innerHTML = `Next Step <svg viewBox="0 0 24 24" class="btn-icon-svg" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>`;
    }

    // Toggle Canvas Mode
    if (currentStep === 4) {
      canvasController.setMode(false); // Disable interactive cropping view
      btnModeSheet.classList.add('active');
      btnModeCrop.classList.remove('active');
      exportActions.classList.remove('hidden');
      renderTypesetSheetOnCanvas();
    } else {
      canvasController.setMode(true); // Enable crop edit workspace
      btnModeCrop.classList.add('active');
      btnModeSheet.classList.remove('active');
      exportActions.classList.add('hidden');
      canvasController.update();
    }
    
    // Auto-fit zoom when switching views
    setTimeout(() => {
      canvasController.resetZoom();
    }, 50);
  }

  btnNext.addEventListener('click', () => {
    if (!canvasController.getSourceImage()) {
      showToast('Please upload a photo first.');
      return;
    }

    if (currentStep < 4) {
      currentStep++;
      updateStepUI();
    } else {
      showToast('Typesetting layout finished. Choose Download or Print options.');
    }
  });

  btnPrev.addEventListener('click', () => {
    if (currentStep > 1) {
      currentStep--;
      updateStepUI();
    }
  });

  stepIndicators.forEach((ind, index) => {
    ind.addEventListener('click', () => {
      const targetStep = index + 1;
      if (targetStep !== currentStep) {
        if (!canvasController.getSourceImage()) {
          showToast('Please upload a photo first.');
          return;
        }
        currentStep = targetStep;
        updateStepUI();
      }
    });
  });

  // --- Step 1: File Loading & Image Loading Setup ---
  function handleImageFile(file: File) {
    if (!file.type.startsWith('image/')) {
      showToast('Please select a valid image file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        canvasController.setImage(img);
        showToast('Image uploaded successfully.');
        currentStep = 2; // Auto advance to crop step
        updateStepUI();
      };
      img.onerror = () => {
        showToast('Failed to load image.');
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  // Drag and Drop
  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('dragover');
  });

  dropzone.addEventListener('dragleave', () => {
    dropzone.classList.remove('dragover');
  });

  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleImageFile(files[0]);
    }
  });

  // Manual File Browsing
  btnBrowse.addEventListener('click', () => {
    fileInput.click();
  });

  fileInput.addEventListener('change', () => {
    const files = fileInput.files;
    if (files && files.length > 0) {
      handleImageFile(files[0]);
    }
  });

  // Preloaded Samples Click Handlers
  sampleCards.forEach(card => {
    card.addEventListener('click', () => {
      const sampleType = card.getAttribute('data-sample');
      const imgElement = sampleType === 'man' ? sampleImgMan : sampleImgWoman;
      
      if (imgElement && imgElement.src) {
        const img = new Image();
        img.onload = () => {
          canvasController.setImage(img);
          showToast(`Sample ${sampleType} portrait loaded.`);
          currentStep = 2;
          updateStepUI();
        };
        img.onerror = () => {
          showToast('Failed to load sample image.');
        };
        img.src = imgElement.src;
      }
    });
  });

  // --- Step 1: Webcam Operations ---
  btnCamera.addEventListener('click', async () => {
    cameraModal.classList.remove('hidden');
    cameraLoading.classList.remove('hidden');
    cameraSelect.innerHTML = '';
    
    try {
      webcamStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: 'user' },
        audio: false
      });
      webcamVideo.srcObject = webcamStream;
      cameraLoading.classList.add('hidden');

      // Populate camera devices dropdown
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      videoDevices.forEach(d => {
        const option = document.createElement('option');
        option.value = d.deviceId;
        option.text = d.label || `Camera ${cameraSelect.length + 1}`;
        cameraSelect.appendChild(option);
      });
    } catch (err: any) {
      console.error(err);
      cameraLoading.classList.add('hidden');
      cameraModal.classList.add('hidden');
      showToast('Camera access denied: ' + err.message);
    }
  });

  // Camera device change selector
  cameraSelect.addEventListener('change', async () => {
    if (webcamStream) {
      webcamStream.getTracks().forEach(t => t.stop());
    }
    
    cameraLoading.classList.remove('hidden');
    try {
      webcamStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: cameraSelect.value } },
        audio: false
      });
      webcamVideo.srcObject = webcamStream;
      cameraLoading.classList.add('hidden');
    } catch (err: any) {
      console.error(err);
      cameraLoading.classList.add('hidden');
      showToast('Failed to switch camera: ' + err.message);
    }
  });

  function stopWebcam() {
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      webcamStream = null;
    }
    webcamVideo.srcObject = null;
    cameraModal.classList.add('hidden');
  }

  btnCloseCamera.addEventListener('click', stopWebcam);

  // Capture Webcam frame
  btnCaptureSnap.addEventListener('click', () => {
    if (!webcamVideo.videoWidth) return;

    // Create offscreen canvas to capture frame
    const snapCanvas = document.createElement('canvas');
    snapCanvas.width = webcamVideo.videoWidth;
    snapCanvas.height = webcamVideo.videoHeight;
    const snapCtx = snapCanvas.getContext('2d');
    
    if (snapCtx) {
      // Draw mirrored video stream frame
      snapCtx.translate(snapCanvas.width, 0);
      snapCtx.scale(-1, 1);
      snapCtx.drawImage(webcamVideo, 0, 0);
      
      const dataUrl = snapCanvas.toDataURL('image/png');
      const img = new Image();
      img.onload = () => {
        canvasController.setImage(img);
        showToast('Photo captured successfully.');
        stopWebcam();
        currentStep = 2; // Advance to Crop
        updateStepUI();
      };
      img.src = dataUrl;
    }
  });

  // --- Step 2: Dimensions & Crop Actions ---
  sizePreset.addEventListener('change', () => {
    const presetVal = sizePreset.value;
    
    if (presetVal === 'custom') {
      customSizeSettings.classList.remove('hidden');
      canvasController.setTargetSize(
        parseFloat(customWidthInput.value),
        parseFloat(customHeightInput.value)
      );
    } else {
      customSizeSettings.classList.add('hidden');
      
      let w = 35, h = 45; // Schengen
      if (presetVal === '1inch') { w = 25; h = 35; }
      else if (presetVal === '2inch') { w = 35; h = 49; }
      else if (presetVal === 'uspassport') { w = 51; h = 51; }
      else if (presetVal === 'chinesepassport') { w = 33; h = 48; }
      
      canvasController.setTargetSize(w, h);
    }
  });

  // Custom size fields validation and updating
  function handleCustomSizeUpdate() {
    const w = parseFloat(customWidthInput.value);
    const h = parseFloat(customHeightInput.value);
    if (w > 10 && h > 10) {
      canvasController.setTargetSize(w, h);
    }
  }

  customWidthInput.addEventListener('input', handleCustomSizeUpdate);
  customHeightInput.addEventListener('input', handleCustomSizeUpdate);

  toggleGuides.addEventListener('change', () => {
    canvasController.setGuidesVisible(toggleGuides.checked);
  });

  btnRotateCCW.addEventListener('click', () => {
    canvasController.rotateCCW();
  });

  btnRotateCW.addEventListener('click', () => {
    canvasController.rotateCW();
  });

  btnFlipH.addEventListener('click', () => {
    canvasController.flipHorizontal();
  });

  btnResetCrop.addEventListener('click', () => {
    canvasController.resetCrop();
    fineRotateSlider.value = '0';
    fineRotateVal.textContent = '0°';
  });

  fineRotateSlider.addEventListener('input', () => {
    const val = parseFloat(fineRotateSlider.value);
    fineRotateVal.textContent = `${val}°`;
    canvasController.setFineRotation(val);
  });

  // --- Step 3: Background & Beautify Filters Controls ---
  toggleAICutout.addEventListener('change', () => {
    const active = toggleAICutout.checked;
    
    if (active) {
      aiLoadingIndicator.classList.remove('hidden');
      toggleAICutout.disabled = true; // disable temporary while loading
      
      canvasController.setAICutout(true, () => {
        aiLoadingIndicator.classList.add('hidden');
        toggleAICutout.disabled = false;
      });
    } else {
      aiLoadingIndicator.classList.add('hidden');
      toggleAICutout.disabled = false;
      canvasController.setAICutout(false);
    }
  });

  // Background color preset buttons
  bgPresetBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      bgPresetBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      
      const color = btn.getAttribute('data-color') || '#ffffff';
      canvasController.setBGColor(color);
    });
  });

  // Custom Color Picker
  bgCustomColor.addEventListener('input', () => {
    bgPresetBtns.forEach(b => b.classList.remove('active'));
    canvasController.setBGColor(bgCustomColor.value);
  });

  toggleVignette.addEventListener('change', () => {
    canvasController.setBGVignette(toggleVignette.checked);
  });

  // Chroma Key background picker (eye dropper canvas interaction)
  btnChromaPicker.addEventListener('click', () => {
    if (isPickingChromaColor) {
      disableChromaColorPick();
    } else {
      enableChromaColorPick();
    }
  });

  function enableChromaColorPick() {
    isPickingChromaColor = true;
    btnChromaPicker.classList.add('btn-primary');
    btnChromaPicker.classList.remove('btn-secondary');
    btnChromaPicker.innerHTML = `<svg class="btn-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg> Click Photo to Sample Color`;
    document.getElementById('main-canvas')?.classList.add('eyedropper-active');
    showToast('Click anywhere on the photo background to select a key color.');
  }

  function disableChromaColorPick() {
    isPickingChromaColor = false;
    btnChromaPicker.classList.add('btn-secondary');
    btnChromaPicker.classList.remove('btn-primary');
    btnChromaPicker.innerHTML = `<svg class="btn-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"/><path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z"/></svg> Select Background Color to Remove`;
    document.getElementById('main-canvas')?.classList.remove('eyedropper-active');
  }

  // Handle clicking on canvas to pick background color
  document.getElementById('main-canvas')?.addEventListener('click', (e) => {
    if (!isPickingChromaColor) return;

    const mouseEvent = e as MouseEvent;
    const canvas = document.getElementById('main-canvas') as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate click coordinates matching internal canvas resolution
    const x = Math.round(((mouseEvent.clientX - rect.left) / rect.width) * canvas.width);
    const y = Math.round(((mouseEvent.clientY - rect.top) / rect.height) * canvas.height);
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const pix = ctx.getImageData(x, y, 1, 1).data;
      const rgb: [number, number, number] = [pix[0], pix[1], pix[2]];
      
      canvasController.setChromaKeyColor(rgb);
      chromaSettings.classList.remove('hidden');
      disableChromaColorPick();
      showToast(`Chroma key color sampled: rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]}). Adjust tolerance if needed.`);
    }
  });

  // Chroma key settings updates
  chromaTolerance.addEventListener('input', () => {
    const val = parseInt(chromaTolerance.value);
    chromaToleranceVal.textContent = val.toString();
    canvasController.setChromaKeySettings({ tolerance: val });
  });

  chromaFeather.addEventListener('input', () => {
    const val = parseInt(chromaFeather.value);
    chromaFeatherVal.textContent = `${val}px`;
    canvasController.setChromaKeySettings({ feather: val });
  });

  // Filters inputs binding
  function updateFilters() {
    canvasController.setFilters({
      brightness: parseInt(filterBrightness.value),
      contrast: parseInt(filterContrast.value),
      saturation: parseInt(filterSaturation.value),
      smooth: parseInt(filterSmooth.value),
      sharpen: parseInt(filterSharpen.value),
      grayscale: filterGrayscale.checked
    });
  }

  filterBrightness.addEventListener('input', () => {
    filterBrightnessVal.textContent = `${filterBrightness.value}%`;
    updateFilters();
  });

  filterContrast.addEventListener('input', () => {
    filterContrastVal.textContent = `${filterContrast.value}%`;
    updateFilters();
  });

  filterSaturation.addEventListener('input', () => {
    filterSaturationVal.textContent = `${filterSaturation.value}%`;
    updateFilters();
  });

  filterSmooth.addEventListener('input', () => {
    filterSmoothVal.textContent = filterSmooth.value;
    updateFilters();
  });

  filterSharpen.addEventListener('input', () => {
    filterSharpenVal.textContent = filterSharpen.value;
    updateFilters();
  });

  filterGrayscale.addEventListener('change', updateFilters);

  btnResetFilters.addEventListener('click', () => {
    filterBrightness.value = '100';
    filterBrightnessVal.textContent = '100%';
    filterContrast.value = '100';
    filterContrastVal.textContent = '100%';
    filterSaturation.value = '100';
    filterSaturationVal.textContent = '100%';
    filterSmooth.value = '0';
    filterSmoothVal.textContent = '0';
    filterSharpen.value = '0';
    filterSharpenVal.textContent = '0';
    filterGrayscale.checked = false;

    canvasController.resetFilters();
    showToast('Filters reset to default.');
  });

  // --- Step 4: Typesetting layout sheet generator ---
  function getLayoutOptions(): LayoutOptions {
    return {
      paperSize: paperSize.value,
      orientation: paperOrientation.value as 'landscape' | 'portrait',
      marginMm: parseFloat(layoutMargin.value),
      gapMm: parseFloat(layoutGap.value),
      showCutLines: toggleCutLines.checked,
      showBorder: togglePhotoBorder.checked,
      quantity: parseInt(photoQuantity.value),
      dpi: 300 // 300 DPI high resolution
    };
  }

  function renderTypesetSheetOnCanvas() {
    if (currentStep !== 4 || !canvasController.getSourceImage()) return;

    // 1. Get offscreen single photo cropped canvas
    const singlePhotoCanvas = canvasController.generateCroppedIDCanvas();
    const targetSize = canvasController.getTargetSize();

    // 2. Typeset the sheet onto our view canvas
    const viewCanvas = document.getElementById('main-canvas') as HTMLCanvasElement;
    const options = getLayoutOptions();
    
    // This call draws directly onto `#main-canvas`
    renderPrintSheet(
      singlePhotoCanvas, 
      targetSize.widthMm, 
      targetSize.heightMm, 
      options, 
      viewCanvas
    );
    
    // Call trigger update for visual indicators
    canvasController.onStateChange?.();
  }

  // Bind typesetting inputs to trigger auto-render
  paperSize.addEventListener('change', renderTypesetSheetOnCanvas);
  paperOrientation.addEventListener('change', renderTypesetSheetOnCanvas);
  
  layoutMargin.addEventListener('input', () => {
    layoutMarginVal.textContent = `${layoutMargin.value}mm`;
    renderTypesetSheetOnCanvas();
  });

  layoutGap.addEventListener('input', () => {
    layoutGapVal.textContent = `${layoutGap.value}mm`;
    renderTypesetSheetOnCanvas();
  });

  toggleCutLines.addEventListener('change', renderTypesetSheetOnCanvas);
  togglePhotoBorder.addEventListener('change', renderTypesetSheetOnCanvas);
  
  photoQuantity.addEventListener('change', renderTypesetSheetOnCanvas);

  // Quantity picker controls
  btnQtyDec.addEventListener('click', () => {
    let qty = parseInt(photoQuantity.value);
    if (qty > 0) {
      qty--;
      photoQuantity.value = qty.toString();
      renderTypesetSheetOnCanvas();
    }
  });

  btnQtyInc.addEventListener('click', () => {
    let qty = parseInt(photoQuantity.value);
    qty++;
    photoQuantity.value = qty.toString();
    renderTypesetSheetOnCanvas();
  });

  // Auto fill calculation
  btnAutoFill.addEventListener('click', () => {
    photoQuantity.value = '0'; // 0 indicates maximum fill capacity
    renderTypesetSheetOnCanvas();
    showToast('Auto-filled sheet layout to maximum printing capacity.');
  });

  // --- View Mode Tabs Toolbar Toggle ---
  btnModeCrop.addEventListener('click', () => {
    currentStep = 2; // go to step 2
    updateStepUI();
  });

  btnModeSheet.addEventListener('click', () => {
    currentStep = 4; // go to step 4
    updateStepUI();
  });

  // View zoom controls
  btnZoomIn.addEventListener('click', () => {
    canvasController.updateZoom(1.1);
  });

  btnZoomOut.addEventListener('click', () => {
    canvasController.updateZoom(0.9);
  });

  btnZoomReset.addEventListener('click', () => {
    canvasController.resetZoom();
  });

  // --- Step 5: Export / Downloads / Print Setup ---
  
  // A. Download Cropped Photo Only
  btnDownloadSingle.addEventListener('click', () => {
    if (!canvasController.getSourceImage()) return;
    
    const singleCanvas = canvasController.generateCroppedIDCanvas();
    const link = document.createElement('a');
    link.download = `id-photo-${canvasController.getTargetSize().widthMm}x${canvasController.getTargetSize().heightMm}.png`;
    link.href = singleCanvas.toDataURL('image/png');
    link.click();
    showToast('Cropped single photo download started.');
  });

  // B. Download Full Typeset Print Sheet
  btnDownloadSheet.addEventListener('click', () => {
    if (currentStep !== 4) return;
    
    const viewCanvas = document.getElementById('main-canvas') as HTMLCanvasElement;
    const link = document.createElement('a');
    link.download = `print-sheet-${paperSize.value}-${paperOrientation.value}.png`;
    link.href = viewCanvas.toDataURL('image/png');
    link.click();
    showToast('Typeset print sheet download started.');
  });

  // C. Print Now
  btnPrint.addEventListener('click', () => {
    if (currentStep !== 4) return;

    const viewCanvas = document.getElementById('main-canvas') as HTMLCanvasElement;
    
    // Assign sheet data to hidden print element
    printImage.src = viewCanvas.toDataURL('image/png');

    // Remove any existing print size override style
    const existingStyle = document.getElementById('print-page-style');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Fetch details of paper dimensions
    const paper = PAPERS[paperSize.value];
    let wMm = paper.widthMm;
    let hMm = paper.heightMm;
    if (paperOrientation.value === 'landscape') {
      wMm = paper.heightMm;
      hMm = paper.widthMm;
    }

    // Inject dynamic CSS to force exact print size scale
    const style = document.createElement('style');
    style.id = 'print-page-style';
    style.innerHTML = `@page { size: ${wMm}mm ${hMm}mm; margin: 0; }`;
    document.head.appendChild(style);

    showToast('Opening print dialog...');
    setTimeout(() => {
      window.print();
    }, 250);
  });
});
